import { useRef, useEffect, useState, ReactElement, useCallback } from 'react';
import { Collapse, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ErrorOutline } from '@mui/icons-material';
import { IPrice } from 'services/api/entities';
import { PricingProvider } from 'services/contexts/PricingContext/PricingProvider';
import {
  useCheckout,
  useStripeProducts,
  useStripeSession,
  useSubscription,
} from 'services/hooks';
import { locations } from 'services/routes/locations';
import { getHomePageUrl } from 'services/helpers/domain';
import DistributionInfo from 'components/DistributionInfo';
import {
  DistributionLicense,
  PaymentIntervalSwitch,
  PricingPageHeader,
  PricingBox,
} from './components';
import {
  StyledAlert,
  StyledContainer,
  StyledOuterCointainer,
  StyledLoadingContainer,
  StyledPricingBoxesContainer,
  StyledSwitchWrapper,
} from './styled';

type PricingProp = {
  isLoggedIn: boolean;
};

const Pricing = ({ isLoggedIn }: PricingProp): ReactElement => {
  const navigate = useNavigate();

  const [stripeError, setStripeError] = useState<Error | null>(null);

  const distributionLicenseRef = useRef<HTMLDivElement>(null);

  const { createCheckoutSession } = useCheckout();
  const { isSubscriptionActive } = useSubscription();
  const { error: userPortalSessionError, createUserPortalSession } =
    useStripeSession();

  const { products, isFetching: areStripeProductsFetching } =
    useStripeProducts();

  useEffect(() => {
    if (!userPortalSessionError) {
      return;
    }
    setStripeError(userPortalSessionError);
  }, [userPortalSessionError]);

  const handleOnClick = useCallback(
    (price: IPrice | null) => {
      const priceId = price?.id ?? null;
      if (!isLoggedIn) {
        const query = priceId ? `?priceId=${priceId}` : `?isFree=true`;
        navigate(locations.general.register.pathname(query));
        return;
      }
      if (isSubscriptionActive) {
        const returnUrl = `${getHomePageUrl()}${locations.subscription.pricing.pathname(
          ''
        )}`;
        createUserPortalSession({ returnUrl });
        return;
      }

      if (!priceId) {
        return;
      }
      setStripeError(null);
      createCheckoutSession(priceId, (error) => {
        setStripeError(error);
      });
    },
    [
      isLoggedIn,
      isSubscriptionActive,
      createCheckoutSession,
      navigate,
      createUserPortalSession,
    ]
  );

  return (
    <PricingProvider>
      <StyledOuterCointainer>
        <Collapse in={!!stripeError}>
          <StyledAlert icon={<ErrorOutline color="error" />}>
            {stripeError && stripeError.message}
          </StyledAlert>
        </Collapse>

        <PricingPageHeader />
        {areStripeProductsFetching || !products ? (
          <StyledLoadingContainer>
            <CircularProgress color="primary" size="4rem" />
          </StyledLoadingContainer>
        ) : (
          <StyledContainer data-testid="pricing-page-container">
            <StyledSwitchWrapper>
              <PaymentIntervalSwitch />
            </StyledSwitchWrapper>
            <StyledPricingBoxesContainer>
              {products.map((product) => (
                <PricingBox
                  key={product.id}
                  product={product}
                  distributionRef={distributionLicenseRef}
                  onClick={handleOnClick}
                />
              ))}
            </StyledPricingBoxesContainer>
            <DistributionInfo ref={distributionLicenseRef} />
            <DistributionLicense />
          </StyledContainer>
        )}
      </StyledOuterCointainer>
    </PricingProvider>
  );
};

export default Pricing;
